import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { LiffShell } from '@/components/liff/LiffShell'
import { WorkerNameCombobox } from '@/components/temp-workers/WorkerNameCombobox'
import { toast } from '@/components/common/Toast'
import { Button, Input, Label, Textarea } from '@/components/ui'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { BILL_LOCATION_LABELS, BILL_LOCATION_VALUES } from '@/constants/bill-location'
import {
  useRecordTempWorkSession,
  useTempWorkerList,
  useTempWorkSessions,
} from '@/modules/temp-worker/services/tempWorker'
import { getApiErrorMessage } from '@/utils/apiErrorMessage'
import { formatPrice } from '@/utils/number'
import { TempWorkSessionRowActions } from '@/components/pages/Admin/TempWorkers/TempWorkSessionRowActions'

export const Route = createFileRoute('/liff/wage')({
  component: LiffWagePage,
})

function LiffWagePage() {
  const [workDate, setWorkDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [location, setLocation] = useState<string>(BILL_LOCATION_VALUES[0])
  const [workerName, setWorkerName] = useState('')
  const [payBaht, setPayBaht] = useState('')
  const [note, setNote] = useState('')

  const { data: workersRes } = useTempWorkerList({ limit: 500, page: 1 })
  const activeWorkers = useMemo(
    () => (workersRes?.data ?? []).filter((w) => w.active),
    [workersRes],
  )

  const selectedMasterId = useMemo(() => {
    const match = activeWorkers.find((w) => w.name === workerName.trim())
    return match?.id
  }, [activeWorkers, workerName])

  const { data: sessionsRes, refetch } = useTempWorkSessions({
    work_date: workDate,
    location,
  })
  const $record = useRecordTempWorkSession()

  const sessions = sessionsRes?.data.sessions ?? []

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const name = workerName.trim()
    if (!name) {
      toast.toastFailed('กรุณาระบุชื่อพนักงาน')
      return
    }
    const pay = parseFloat(payBaht)
    if (Number.isNaN(pay) || pay < 0) {
      toast.toastFailed('กรุณาระบุค่าจ้างที่ถูกต้อง')
      return
    }
    try {
      await $record.mutateAsync({
        work_date: workDate,
        pay_baht: pay,
        temp_worker_id: selectedMasterId,
        ...(!selectedMasterId ? { worker_name: name } : {}),
        location,
        ...(note.trim() ? { note: note.trim() } : {}),
      })
      toast.toastSuccess('บันทึกค่าจ้างเรียบร้อย')
      setWorkerName('')
      setPayBaht('')
      setNote('')
      refetch()
    } catch (err: unknown) {
      toast.toastFailed(getApiErrorMessage(err, 'บันทึกไม่สำเร็จ'))
    }
  }

  return (
    <LiffShell title="บันทึกค่าจ้าง">
      <form onSubmit={onSubmit} className="mx-auto max-w-md space-y-5">
        <Field label="วันที่ทำงาน">
          <Input
            type="date"
            className="h-12 text-base"
            value={workDate}
            onChange={(e) => setWorkDate(e.target.value)}
            required
          />
        </Field>

        <Field label="สาขา">
          <Select value={location} onValueChange={setLocation}>
            <SelectTrigger className="h-12 text-base">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {BILL_LOCATION_VALUES.map((loc) => (
                <SelectItem key={loc} value={loc} className="text-base py-3">
                  {BILL_LOCATION_LABELS[loc]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="ชื่อพนักงาน">
          <WorkerNameCombobox
            id="liff-wage-worker-name"
            workers={activeWorkers}
            value={workerName}
            onChange={setWorkerName}
            size="large"
            placeholder="เลือกหรือพิมพ์ชื่อพนักงาน"
            searchPlaceholder="ค้นหาชื่อ เบอร์ หรือตำแหน่ง"
            fromListHeading="จากรายชื่อที่บันทึกไว้"
            listEmptyMessage="ไม่พบชื่อในรายการ"
            formatCustomNameLabel={(name) => `ใช้ชื่อ "${name}"`}
          />
        </Field>

        <Field label="ค่าจ้าง (บาท)">
          <Input
            type="number"
            inputMode="decimal"
            min={0}
            step="1"
            className="h-12 text-lg font-semibold"
            value={payBaht}
            onChange={(e) => setPayBaht(e.target.value)}
            placeholder="0"
            required
          />
        </Field>

        <Field label="หมายเหตุ (ถ้ามี)">
          <Textarea
            className="min-h-[88px] resize-y text-base"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="รายละเอียดเพิ่มเติม"
          />
        </Field>

        <Button
          type="submit"
          className="h-14 w-full text-lg font-semibold"
          disabled={$record.isLoading}
        >
          {$record.isLoading ? 'กำลังบันทึก...' : 'บันทึกค่าจ้าง'}
        </Button>
      </form>

      <section className="mx-auto mt-8 max-w-md">
        <h2 className="mb-3 text-base font-semibold text-slate-700">
          รายการวันนี้ ({sessions.length} รายการ)
        </h2>
        {sessions.length === 0 ? (
          <p className="rounded-xl bg-white px-4 py-6 text-center text-slate-500">ยังไม่มีรายการ</p>
        ) : (
          <ul className="space-y-2">
            {sessions.map((s) => (
              <li
                key={s.id}
                className="flex items-center justify-between rounded-xl border border-border/50 bg-white px-4 py-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <span className="truncate font-medium text-slate-800">{s.worker_name_snapshot}</span>
                      {s.note?.trim() ? (
                        <p className="mt-0.5 line-clamp-2 text-sm text-slate-500">{s.note}</p>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-bold text-emerald-700">
                        {formatPrice(s.pay_baht ?? 0)} ฿
                      </span>
                      <TempWorkSessionRowActions
                        session={s}
                        paidThroughWorkDate={
                          s.temp_worker_id
                            ? activeWorkers.find((w) => w.id === s.temp_worker_id)
                                ?.paid_through_work_date ?? null
                            : null
                        }
                        compact
                        onSuccess={(kind, next) => {
                          if (kind === 'update') {
                            setWorkDate(next.work_date)
                            if (next.location) setLocation(next.location)
                          }
                          void refetch()
                        }}
                      />
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </LiffShell>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <Label className="text-base font-medium text-slate-700">{label}</Label>
      {children}
    </div>
  )
}
